import { Action } from 'history';
import * as React from 'react';

import {
    type LocationDescriptor,
    type NavigationScreenOpenAPISpecState,
    type NavigationScreenState,
    resolveLocationDescriptorToURL,
    resolveLocationToNavigationScreen,
    resolveNavigationScreenToLocation,
} from '@gitbook/app-navigation';
import { Logger } from '@gitbook/util-debug';
import { localStorage } from '@gitbook/util-local-storage';

import { NavigationFunctionsContext, NavigationStateContext } from './contexts';
import { getNavigationLocationFor } from './getNavigationLocationFor';
import { getNavigationStateFactory } from './getNavigationState';
import { useHistory, useHistoryLocation } from './history';
import type {
    NavigateFunction,
    NavigateGetLocationFunction,
    NavigateURLFunction,
    NavigationState,
    NavigationTarget,
} from './types';

const logger = Logger('navigation');

export const LAST_SEEN_CONTENT_KEY = '@content:last-seen';
const LAST_SEEN_OPENAPI_KEY = '@openapi:last-seen';
const LAST_SEEN_ORG_KEY = '@org:last-seen';

/**
 * Context provider.
 */
export function NavigationProvider(props: {
    children: React.ReactNode;

    /**
     * If true, the browser will do hard navigate to the location.
     * it should be true on public content when network is not reliable and we want SSR as source of truth.
     */
    forceHardNavigation?: boolean;

    persist: boolean;
    getNavigationScreenFromLocation?: (location: LocationDescriptor) => NavigationScreenState;
    getLocationForNavigationScreen?: (screen: NavigationScreenState) => LocationDescriptor;
}): React.ReactElement {
    const {
        children,
        persist,
        forceHardNavigation = false,
        getNavigationScreenFromLocation = resolveLocationToNavigationScreen,
        getLocationForNavigationScreen = resolveNavigationScreenToLocation,
    } = props;

    const history = useHistory();
    const location = useHistoryLocation();

    const [_, startTransition] = React.useTransition();

    /**
     * Get the navigation state from a location.
     */
    const getNavigationState = React.useCallback(
        getNavigationStateFactory({
            getNavigationScreenFromLocation,
            getLocationForNavigationScreen,
        }),
        [getNavigationScreenFromLocation, getLocationForNavigationScreen]
    );
    /**
     * Everytime we navigate, we will persist the navigation state into a navigationStack.
     */
    const navigationStack = React.useRef<NavigationState[]>([]);
    React.useEffect(() => {
        const unlisten = history.listen(({ location, action }) => {
            const state = getNavigationState(location);

            logger.info(`history action: ${action}`, { location, state });

            switch (action) {
                case Action.Push:
                    navigationStack.current.push(state);
                    break;
                case Action.Pop:
                    navigationStack.current.pop();
                    navigationStack.current[navigationStack.current.length - 1] = state;
                    break;
                case Action.Replace:
                    navigationStack.current.pop();
                    navigationStack.current.push(state);
                    break;
            }
        });
        return unlisten;
    }, [history, getNavigationState]);

    const navigationState = React.useMemo(
        () => getNavigationState(location),
        [getNavigationState, location]
    );

    /**
     * Persist the user's navigation state between sessions.
     */
    React.useEffect(() => {
        if (persist) {
            persistNavigationState(navigationState);
        }
    }, [navigationState, persist]);

    const functions = React.useMemo(() => {
        const getRawLocationFor = (to: NavigationTarget) => {
            return getNavigationLocationFor(
                getNavigationState(history.location),
                to,
                navigationStack.current
            );
        };

        /**
         * Method to navigate to another url
         */
        const navigate: NavigateFunction = (to, options) => {
            const doNavigate = () => {
                history.transaction(() => {
                    const next = getRawLocationFor(to);

                    if (!next) {
                        return;
                    }
                    const nextLocation = getLocationForNavigationScreen(next.location);

                    if (
                        // Before calling .go(), we check that the transaction will not be blocked
                        // As .go() will always work and could result in an infinite loop in the history
                        !history.isBlocked(nextLocation) &&
                        next.go !== 0
                    ) {
                        logger.info('go to', next.go);
                        history.go(next.go);
                    }

                    const nextState = (() => {
                        if (!next.background) {
                            return null;
                        }
                        const location = getLocationForNavigationScreen(next.background);
                        return {
                            background: `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`,
                        };
                    })();

                    // Nothing changed ?
                    if (
                        history.location.pathname === nextLocation.pathname &&
                        history.location.search === nextLocation.search &&
                        history.location.hash === nextLocation.hash &&
                        nextState?.background === history.location.state?.background
                    ) {
                        return;
                    }

                    if (nextLocation.pathname.startsWith('https://')) {
                        const url = resolveLocationDescriptorToURL(nextLocation.pathname, {
                            search: nextLocation.search,
                            hash: nextLocation.hash,
                        });
                        logger.info(`navigate externally with "${url.toString()}"`, nextState);
                        window.location.href = url;
                    } else {
                        // Force navigate to the new location if configured like this
                        // we don't do it for search/hash updates where the Push history will work
                        if (
                            forceHardNavigation &&
                            nextLocation.pathname !== history.location.pathname
                        ) {
                            window.location.href = resolveLocationDescriptorToURL(
                                `${window.location.protocol}//${window.location.host}`,
                                nextLocation
                            );
                            return;
                        }

                        if (options?.replace) {
                            logger.info(`replace with "${nextLocation.pathname}"`, {
                                location: nextLocation,
                                state: nextState,
                            });
                            history.replace({ ...nextLocation, state: nextState ?? undefined });
                        } else {
                            logger.info(`push "${nextLocation.pathname}"`, {
                                location: nextLocation,
                                state: nextState,
                            });
                            history.push({ ...nextLocation, state: nextState ?? undefined });
                        }
                    }
                });
            };

            if (options?.suspenseTransition) {
                startTransition(() => {
                    doNavigate();
                });
            } else {
                doNavigate();
            }
        };

        /**
         * Method to get the pathname of navigation to another url
         */
        const getNavigationURL: NavigateURLFunction = (to) => {
            const { pathname, search, hash } = getLocationFor(to);
            return `${pathname}${search || ''}${hash ? `#${hash}` : ''}`;
        };

        /**
         * Method to get the location of navigation to another url
         */
        const getLocationFor: NavigateGetLocationFunction = (to) => {
            const next = getRawLocationFor(to);

            if (!next) {
                return history.location;
            }

            const location = getLocationForNavigationScreen(next.location);
            const state = next.background
                ? { background: getLocationForNavigationScreen(next.background).pathname }
                : undefined;

            return {
                ...location,
                state,
            };
        };

        return {
            forceHardNavigation,
            navigate,
            getNavigationURL,
            getLocationFor,
        };
    }, [history, forceHardNavigation, getNavigationState, getLocationForNavigationScreen]);

    return (
        <NavigationStateContext.Provider value={navigationState}>
            <NavigationFunctionsContext.Provider value={functions}>
                {children}
            </NavigationFunctionsContext.Provider>
        </NavigationStateContext.Provider>
    );
}

/**
 * Return the current state of the application.
 */
export function useNavigationState(): NavigationState {
    return React.useContext(NavigationStateContext);
}

/**
 * Return the navigation functions.
 */
export function useNavigationFunctions() {
    return React.useContext(NavigationFunctionsContext);
}

/**
 * Persist the navigation state.
 */
async function persistNavigationState(state: NavigationState): Promise<void> {
    if ('organization' in state.screen && state.screen.organization) {
        // If the state is an organization, we persist it.
        await localStorage.set(LAST_SEEN_ORG_KEY, state.screen.organization);
    }

    // If the state indicates a content change (like a space, collection),
    // then we persist it.
    switch (state.screen.type) {
        case 'space':
        case 'home':
        case 'site': {
            const persistedState = {
                ...state,
                overlay: undefined,
            };
            await localStorage.set(LAST_SEEN_CONTENT_KEY, persistedState);
            break;
        }
        case 'openapi_spec': {
            await persistLastSeenOpenAPIScreen(state.screen);
            break;
        }
        default:
            break;
    }
}

/**
 * Persist the last seen OpenAPI state in the local storage.
 */
async function persistLastSeenOpenAPIScreen(screen: NavigationScreenOpenAPISpecState) {
    await localStorage.set(LAST_SEEN_OPENAPI_KEY, screen);
}

/**
 * Get the last OpenAPI screen seen by the user.
 */
export function getLastSeenOpenAPIScreen(): NavigationScreenOpenAPISpecState | null {
    return localStorage.get(LAST_SEEN_OPENAPI_KEY);
}

/**
 * Get the latest seen organization id.
 */
export function getLastSeenOrgId(): string | null {
    return localStorage.get(LAST_SEEN_ORG_KEY);
}
